import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Spin,
} from 'antd';

import dayjs from 'dayjs';
import { withTranslation } from 'react-i18next';

import * as ArrayUtils from '@utils/array';
import * as OwnershipActions from '@actions/ownerships';

const RESET_STATE = (state = {}) => {
  return {
    isFetchingOwnerships: false,
    ownershipsById: {},
    errorFetchingOwnerships: null,
    ...state,
  };
}

class CreateForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = RESET_STATE();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.visible && !this.props.visible) {
      this.props.form.resetFields();
      this.setState(RESET_STATE());
      return
    }
    if (!prevProps.visible && this.props.visible) {
      this.setState({ isFetchingOwnerships: true });
      let params = { ownership_id: this.props.ownershipId };
      this.props.fetchOwnershipsWithManagers(params).then((responseData) => {
        this.setState({
          isFetchingOwnerships: false,
          ownershipsById: {
            ...ArrayUtils.arrayToObject(responseData.ownerships),
          },
        });
      }).catch((error) => {
        this.setState({
          isFetchingOwnerships: false,
          errorFetchingOwnerships: error,
        });
      });
    }
  }

  render() {
    const { visible, onCancel, onOk, confirmLoading, form } = this.props;
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
      },
    };
    return (
      <Modal
        visible={ visible }
        title={ this.props.t("Tenancies.actions.AddTenancy") }
        okText={ this.props.t("actions.Submit") }
        onCancel={ onCancel }
        onOk={ onOk }
        confirmLoading={ confirmLoading }
      >
        <Form form={this.props.form} layout="horizontal">
          <Form.Item
            label={ this.props.t("Properties.Property") }
            {...formItemLayout}
           name="ownership_id" rules={[
                  { required: true, message: `${ this.props.t("Properties.messages.Form.PleaseSelectProperty") }!` },
                ]}>
              <Select
                  style={{ width: "100%" }}
                  placeholder={ this.props.t("Properties.messages.Form.PleaseSelectProperty") }
                  loading={ this.state.isFetchingOwnerships }
                  showSearch={ (Object.values(this.state.ownershipsById).length > 5) }
                  filterOption={ (inputValue, option) => {
                    const ownershipId = parseInt(option.key);
                    const ownership = this.state.ownershipsById[ownershipId];
                    if (ownership.name.toLowerCase().includes(inputValue.toLowerCase())) {
                      return true;
                    }
                    if (ownership.address.toLowerCase().includes(inputValue.toLowerCase())) {
                      return true;
                    }
                    return false;
                  }}
                  notFoundContent={
                    this.state.isFetchingOwnerships ? <Spin size="small" /> : null
                  }
                >
                  {
                    Object.values(this.state.ownershipsById).map((ownership) => (
                      <Select.Option
                        key={ ownership.id.toString() }
                      >
                        { ownership.name }. { ownership.address }
                      </Select.Option>
                    ))
                  }
                </Select>
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenancyPeriod") }
             {...formItemLayout}
           name="contract_period" rules={[{ type: 'array', required: true, message: 'Please select dates!' }]}>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.RentFreePeriod") }
            {...formItemLayout}
           name="rent_free_period" rules={[{ type: 'array', required: false, message: 'Please select dates!' }]}>
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenantName") }
            {...formItemLayout}
             name="official_tenant_name" rules={[{ required: true, message: "Please input registered tenant name" }]}>
            <Input placeholder={ this.props.t("Tenancies.TenantName") } />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenantEmail") }
            {...formItemLayout}
             name="official_tenant_email" rules={[{ required: false, message: "Please input registered tenant email" }]}>
            <Input placeholder={ this.props.t("Tenancies.TenantEmail") } />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenantPhone") }
            {...formItemLayout}
             name="official_tenant_phone_number" rules={[{ required: false, message: "Please input registered tenant phone number" }]}>
            <Input placeholder={ this.props.t("Tenancies.TenantPhone") } />
          </Form.Item>
          <Form.Item
            label={ `${this.props.t("Tenancies.TenantCorrespondenceAddress") } (${this.props.t("Tenancies.IfDifferentToPropertyAddress")})` }
            {...formItemLayout}
           name="correspondence_address" rules={[{ required: false, message: "Please input registered tenant correspondence address" }]}>
            <Input.TextArea
                rows={ 3 }
              />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.PaymentDate") }
            {...formItemLayout}
           name="rent_due" rules={[{ required: true, message: 'Please input payment due date' }]} initialValue={1}>
            <InputNumber
                style={{ width: '100%', minWidth: '150px' }}
                min={1}
                max={31}
              />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.RentAmount") }
            {...formItemLayout}
           name="rental_amount" rules={[{ required: true, message: 'Please input rental amount' }]}>
            <InputNumber
                  style={{ width: '100%', minWidth: '150px' }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
          </Form.Item>
          <Form.Item
            label={ this.props.t("shared.SecurityDeposit") }
            {...formItemLayout}
           name="security_deposit" rules={[{ required: true, message: 'Please input security deposit' }]}>
            <InputNumber
                  style={{ width: '100%', minWidth: '150px' }}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenancyTerms.ManagementFee") }
            {...formItemLayout}
           name="include_mgmt_fee" rules={[{ required: true, message: 'Please select management fee' }]}>
            <Radio.Group>
                <Radio value="true">Included</Radio>
                <Radio value="false">Excluded</Radio>
              </Radio.Group>
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenancyTerms.GovtRent") }
            {...formItemLayout}
           name="include_govt_rent" rules={[{ required: true, message: 'Please select govt rent' }]}>
            <Radio.Group>
                <Radio value="true">Included</Radio>
                <Radio value="false">Excluded</Radio>
              </Radio.Group>
          </Form.Item>
          <Form.Item
            label={ this.props.t("Tenancies.TenancyTerms.GovtRates") }
            {...formItemLayout}
           name="include_govt_rates" rules={[{ required: true, message: 'Please select govt rates' }]}>
            <Radio.Group>
                <Radio value="true">Included</Radio>
                <Radio value="false">Excluded</Radio>
              </Radio.Group>
          </Form.Item>
          <Form.Item
            hasFeedback
            label={ this.props.t("shared.Notes") }
            {...formItemLayout}
           name="notes">
            <Input.TextArea rows={ 5 } />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {};
}

const mapDispatchToProps = (dispatch, props) => {
  return {
    fetchOwnershipsWithManagers: (params) => {
      return (dispatch(OwnershipActions.fetchOwnershipsWithManagers(params)));
    },
  };
}

const ConnectedCreateForm = connect(mapStateToProps, mapDispatchToProps)(CreateForm);

const WrappedCreateForm = (props) => {
  const [form] = Form.useForm();
  const { formRef, ...restProps } = props;
  if (formRef) formRef(form);
  return <ConnectedCreateForm {...restProps} form={form} />;
};
export default withTranslation('common')(WrappedCreateForm);
